import React from "react";
import { reduxForm, InjectedFormProps, FormProps } from "redux-form";
import { connect } from "react-redux";
import { Grid, Typography } from "material-ui";
import { Link } from "react-router-dom";
import EditIcon from "material-ui-icons/Edit";

import ExtendedField from "../../components/form/ExtendedField";
import renderTextfield from "../../components/form/RenderTextField";
import { ReduxState, ReduxDispatch } from "../../ducks/reducers";
import LoginPageForm, { LoginFormData } from "./components/LoginPageForm";
import {
    login,
    validatePassCode,
    createPassCodeKey,
    getLoginTimeout,
} from "../../ducks/user";
import { RouterProps } from "react-router";
import {
    SetShowBackNavigation,
    SetHeader,
    ShowBackNavigationWithRoute,
} from "../../ducks/common";
import { SetActionButtonAndShow } from "../../ducks/actionbutton";
import logo from "images/hypo-logo-complete-blue.svg";
import logger from "util/logger";
import { getSecureStorage } from "util/storage";
import { CodeDialog } from "./components/CodeDialog";
import { IsCordova } from "util/platformUtils";
import { IPasskeyCreateResponse } from "util/interfaces";

const styles = {
    flex: {
        flex: 1,
    },
    menuButton: {
        marginLeft: 0,
        marginRight: -12,
    },
};

interface ILoginPageProps {}
interface ILoginPageReduxProps {}
interface ILoginPageDispatchProps {
    login: (username: string, password: string) => Promise<void>;
    setBackButton: (route: string) => void;
    hideBackButton: () => void;
    setHeader: (header: string) => void;
    validateCode: (code: string, hash: string) => Promise<string>;
    createKey: (code: string) => Promise<IPasskeyCreateResponse>;
    loginTimeout: () => Promise<number | undefined>;
}

interface LoginPageState {
    canUsePasscode: boolean;
    setPasscode: boolean;
    verifyPasscode: boolean;
    code: string | undefined;
    credentials: LoginFormData | undefined;
    attemptTimeout?: number;
}

type LoginPageProps = ILoginPageProps &
    ILoginPageReduxProps &
    ILoginPageDispatchProps &
    RouterProps;

export class LoginPage extends React.Component<LoginPageProps, LoginPageState> {
    constructor(props: LoginPageProps) {
        super(props);
        this.state = {
            canUsePasscode: false,
            setPasscode: false,
            verifyPasscode: false,
            code: undefined,
            credentials: undefined,
        };
    }

    componentWillMount() {
        if (IsCordova) {
            this.canLoginWithCode();
        }
    }

    componentDidMount() {
        this.props.hideBackButton();
        this.props.setHeader("Mobiilipankki");
        this.props
            .loginTimeout()
            .then((res) => this.setState({ attemptTimeout: res }));
    }

    componentWillUnmount() {
        this.props.setHeader("");
    }

    savePasscode = (key: string) => {
        this.props
            .createKey(key)
            .then((keyResponse: IPasskeyCreateResponse) => {
                let secureStorage = getSecureStorage();
                if (secureStorage && this.state.credentials) {
                    secureStorage.set(
                        (k: string) => {
                            secureStorage.set(
                                (val: string) => {
                                    localStorage.setItem("PassCodeSet", "YES");
                                    this.setState({
                                        ...this.state,
                                        credentials: undefined,
                                    });
                                    this.props.history.replace("/");
                                },
                                (error: any) => {
                                    logger.error("Error " + error);
                                    this.props.history.replace("/");
                                },
                                key,
                                keyResponse.hash
                            );
                        },
                        (error: any) => {
                            this.setState({
                                ...this.state,
                                credentials: undefined,
                            });
                            logger.error("Error " + error);
                            this.props.history.replace("/");
                        },
                        keyResponse.key,
                        JSON.stringify({
                            password: this.state.credentials.password,
                            username: this.state.credentials.username,
                        })
                    );
                } else {
                    this.props.history.replace("/");
                }
            })
            .catch(() => {
                this.props.history.replace("/");
            });
    };

    login = async (data: LoginFormData) => {
        await this.props
            .login(data.username, data.password)
            .then(() => {
                if (
                    !this.state.canUsePasscode &&
                    IsCordova &&
                    getSecureStorage()
                ) {
                    this.setState({
                        ...this.state,
                        setPasscode: true,
                        credentials: data,
                    });
                } else {
                    this.props.history.replace("/");
                }
            })
            .catch((error) => {
                if (error.status === 429) {
                    this.props
                        .loginTimeout()
                        .then((res) => this.setState({ attemptTimeout: res }))
                        .catch(() => {
                            this.setState({ attemptTimeout: 60000 });
                        });
                }
            });
    };

    loginWithCode(code: string, success: Function, failure: Function) {
        let secureStorage = getSecureStorage();
        if (secureStorage) {
            secureStorage.get(
                (value: string) => {
                    this.props
                        .validateCode(code, value)
                        .then((key: string) => {
                            secureStorage.get(
                                (val: string) => {
                                    let credentials = JSON.parse(
                                        val
                                    ) as LoginFormData;
                                    this.login(credentials)
                                        .then(() => {
                                            this.props.history.replace("/");
                                        })
                                        .catch((err: Error) => {
                                            logger.error(err);
                                        });
                                },
                                (error: any) => {
                                    failure("");
                                    logger.error("Error " + error);
                                },
                                key
                            );
                        })
                        .catch((err: Error) => {
                            failure("");
                        });
                },
                function (error: any) {
                    failure("");
                    logger.error("Error " + error);
                },
                code
            );
        }
    }

    clearSecureStorage() {
        let secureStorage = getSecureStorage();
        if (secureStorage) {
            secureStorage.clear(
                function () {
                    logger.info("cleared");
                },
                function (error: any) {
                    logger.info("Error, " + error);
                }
            );
        }
    }
    setPasscode(code: string, success: Function, failure: Function) {
        if (code.length === 4 && /^\d+$/.test(code)) {
            this.setState({
                ...this.state,
                setPasscode: false,
                verifyPasscode: true,
                code: code,
            });
            success();
        }
        failure("");
    }

    verifyPasscode(
        code: string,
        verifyCode: string,
        success: Function,
        failure: Function
    ) {
        if (
            code === verifyCode &&
            verifyCode.length === 4 &&
            /^\d+$/.test(verifyCode)
        ) {
            success();
            this.setState({
                ...this.state,
                setPasscode: false,
                verifyPasscode: false,
            });
            // save passcode
            this.savePasscode(verifyCode);
        } else {
            logger.error(
                "verifyPasscode(): Unable to save passcode, no secure storage found"
            );
            this.props.history.replace("/");
        }
        failure("Tunnusluvut eivät vastaa toisiaan");
    }

    canLoginWithCode() {
        if (
            IsCordova &&
            localStorage.getItem("PassCodeSet") &&
            !sessionStorage.getItem("logout")
        ) {
            this.setState({ ...this.state, canUsePasscode: true });
        } else {
            this.setState({ ...this.state, canUsePasscode: false });
        }
    }

    renderCodeLogin() {
        if (IsCordova) {
            return (
                <CodeDialog
                    title="Syötä tunnusluku"
                    description=""
                    showSkip={false}
                    onClose={() => {}}
                    submit={(code, success, failure) => {
                        this.loginWithCode(code, success, failure);
                    }}
                />
            );
        }
        return <div />;
    }

    renderSetPasscodeModal() {
        if (IsCordova) {
            return (
                <CodeDialog
                    title="Aseta tunnusluku"
                    description="Kun asetat tunnusluvun, voit jatkossa kirjautua tällä laitteella käyttämällä tunnuslukua."
                    showSkip={true}
                    onClose={() => {
                        this.clearSecureStorage();
                        this.props.history.replace("/");
                    }}
                    submit={(code, success, failure) => {
                        this.setPasscode(code, success, failure);
                    }}
                />
            );
        }
        return <div />;
    }

    renderVerifyPasscodeModal(previousCode: string) {
        if (IsCordova) {
            return (
                <CodeDialog
                    title="Vahvista tunnusluku"
                    description="Kirjoita valitsemasi pin koodi uudelleen."
                    showSkip={false}
                    onClose={() => {
                        this.clearSecureStorage();
                        this.props.history.replace("/");
                    }}
                    submit={(code, success, failure) => {
                        this.verifyPasscode(
                            previousCode,
                            code,
                            success,
                            failure
                        );
                    }}
                />
            );
        }
        return <div />;
    }

    render() {
        return (
            <div className="LoginPage_Container container-with-logo">
                {IsCordova &&
                    this.state.setPasscode &&
                    this.renderSetPasscodeModal()}
                {IsCordova &&
                    this.state.verifyPasscode &&
                    this.state.code &&
                    this.renderVerifyPasscodeModal(this.state.code)}
                {IsCordova &&
                    this.state.canUsePasscode &&
                    (!this.state.setPasscode || !this.state.verifyPasscode) &&
                    this.renderCodeLogin()}
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                    }}
                >
                    <Grid container spacing={0} justify="center">
                        <Grid item xs={12} sm={10} md={6}>
                            <Typography
                                variant="title"
                                color="inherit"
                                style={styles.flex}
                            >
                                <img
                                    className={"loginLogo"}
                                    src={logo}
                                    alt={"HYPO image logo"}
                                />
                            </Typography>
                            <h1
                                className={"loginHeader"}
                                style={{ color: "#646363" }}
                            >
                                Kirjautuminen
                            </h1>
                            <LoginPageForm
                                form="login"
                                attemptTimeout={this.state.attemptTimeout}
                                onAttemptTimeoutExpire={() => {
                                    this.setState({
                                        attemptTimeout: undefined,
                                    });
                                }}
                                onSubmit={this.login}
                            />
                            <Grid item xs={12}>
                                <Link
                                    className={"bluelink"}
                                    to={"/eidentify/1"}
                                >
                                    <h2
                                        style={{ fontSize: "16px" }}
                                        className={"loginHeader"}
                                    >
                                        Rekisteröidy
                                    </h2>
                                </Link>
                                <Link
                                    className={"bluelink"}
                                    to={"/eidentify/2"}
                                >
                                    <h2
                                        style={{ fontSize: "16px" }}
                                        className={"loginHeader"}
                                    >
                                        Kirjaudu pankkitunnuksilla tai mobiilivarmenteella
                                    </h2>
                                </Link>
                            </Grid>
                        </Grid>
                    </Grid>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: ReduxState): ILoginPageReduxProps => {
    return {};
};

const mapDispatchToProps = (
    dispatch: ReduxDispatch
): ILoginPageDispatchProps => {
    return {
        login: async (username: string, password: string) => {
            await dispatch(login(username, password));
        },
        setBackButton: (route: string) => {
            dispatch(ShowBackNavigationWithRoute(route));
        },
        hideBackButton: () => {
            dispatch(SetShowBackNavigation(false));
        },
        setHeader: (header: string) => {
            dispatch(SetHeader(header));
        },
        validateCode: async (code: string, hash: string) => {
            return dispatch(validatePassCode(code, hash));
        },
        createKey: async (code: string) => {
            return dispatch(createPassCodeKey(code));
        },
        loginTimeout: async () => {
            return dispatch(getLoginTimeout());
        },
    };
};

export default connect<
    ILoginPageReduxProps,
    ILoginPageDispatchProps,
    ILoginPageProps
>(
    mapStateToProps,
    mapDispatchToProps
)(LoginPage);



// WEBPACK FOOTER //
// ./src/views/login/LoginPage.tsx